import {
  ActivateCardResultTypenames,
  CardTypeEnum,
  ChangeCardPinInput,
  ChangeCardPinResultTypenames,
  Mutation,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { requestContentCardsRefresh } from 'services/engagement/braze/braze';
import changeCardPin from 'services/graphql/mutations/ChangeCardPin';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Pin from 'shared/components/vxl/Pin/Pin';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles, {
  useRefreshSystemBanners,
} from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useEngagement } from 'shared/hooks/useBraze';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useMutation } from 'urql';

import { PinSetSource } from './PinSetSource';

type ResultTypenames = keyof typeof ActivateCardResultTypenames;

type Params = {
  title: string;
  message: string;
  type: ResultTypenames;
  cardId: string;
  cardType: string;
  source?: PinSetSource;
  shouldShowWalletButton?: string;
};

const newPin = 'new-pin';
const changePin = 'change-pin';

const PinSetScreen = () => {
  const router = useRouter();
  const { setSnackBarMessage } = useContext(MyVaroContext);

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'activationCard',
  });
  const {
    title,
    message,
    type,
    cardId,
    cardType,
    source,
    shouldShowWalletButton,
  } = useLocalSearchParams<Params>();

  const [pin, setPin] = useState<string>('');
  const [confirmPin, setConfirmPin] = useState<string>('');
  const [description, setDescription] = useState<string>('pinDescription');
  const [confirm, setConfirm] = useState(false);
  const [resultState, setResultState] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const [, executeChangeCardPinMutation] = useMutation<Mutation>(changeCardPin);
  const { data, error, fetching, refreshSystemBanners } =
    useRefreshSystemBanners();

  const pathName = usePathname();
  const { bottom } = useSafeAreaInsets();

  const { fetching: tilesFetching, refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHARGE_CARD,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BANKING,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_WELCOME,
  ]);

  const isRefreshLoading = fetching || tilesFetching;

  const { logCustomEvent } = useEngagement();

  let pinTitle = '';
  if (pathName.includes(newPin)) {
    pinTitle = 'newPinTitle';
  } else if (pathName.includes(changePin)) {
    pinTitle = 'changePinTitle';
  } else {
    pinTitle = 'title';
  }

  useViewDidFocusScreenAnalyticsEvent({
    key: 'Card Management Create Pin',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  const handleOnCardPinButtonAnalytics = usePressableAnalytics({
    analyticsId: 'pinSetSubmitted',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  const cardActivationSuccessEvent = usePressableAnalytics({
    analyticsId: 'cardActivationSuccessful',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  const handlePinSetSuccessAnalytics = usePressableAnalytics({
    analyticsId: 'pinSetCompleted',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  const handlePinSetFailureAnalytics = usePressableAnalytics({
    analyticsId: 'pinSetFailed',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  useEffect(() => {
    if (!isRefreshLoading && (data || error)) {
      if (type === undefined && pathName.includes(newPin)) {
        setSnackBarMessage?.(translatedText('newPinSuccessMessage'));

        if (source === PinSetSource.ProgressChecklist) {
          router.dismissTo({
            pathname: `/`,
            params: {
              refreshTileId: HomeTileTypeIds.VXL_CARD_GROUP_TILE_WELCOME,
            },
          });
        } else {
          router.dismissTo({
            pathname: `/myvaro/card/[cardId]`,
            params: {
              cardId: cardId,
            },
          });
        }
      } else if (type === undefined && pathName.includes(changePin)) {
        setSnackBarMessage?.(translatedText('changePinSuccessMessage'));
        router.dismissTo({
          pathname: `/myvaro/card/[cardId]`,
          params: {
            cardId: cardId,
          },
        });
      } else {
        cardActivationSuccessEvent();
        logCustomEvent('BRAZETRIGGER, card activation success pin set');
        requestContentCardsRefresh();

        router.replace({
          pathname: `/myvaro/card/activate-card-success`,
          params: {
            type: type,
            cardId: cardId,
            title: title,
            message:
              cardType === CardTypeEnum.DebitCard
                ? translatedText('defaultPinSetSuccessMessage') // use explicit message for debit cards - see JT-154523
                : message,
            cardType: cardType,
            // Default to showing wallet button unless explicitly disabled via route params.
            // Note: During card activation, Visa may not have provisioned the card UUID yet,
            // causing wallet provisioning to fail for debit cards. The activation flow passes
            // 'false' to hide the button. Believe cards are always shown. If the user comes
            // directly into the set PIN screen, this means the card is already activated, so the
            // wallet button for debit cards can be safely shown.
            shouldShowWalletButton: shouldShowWalletButton,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshLoading, data, error, router]);

  const cardPinChange = async (
    idempotencyId: string,
    input: ChangeCardPinInput,
  ) => {
    handleOnCardPinButtonAnalytics();
    setResultState(true);

    const result = await executeChangeCardPinMutation({
      idempotencyId,
      input,
    });

    const changeCardPinData = result?.data?.changeCardPin;
    const changeCardPinTypename = changeCardPinData?.__typename;
    if (
      changeCardPinTypename ===
      ChangeCardPinResultTypenames.ChangeCardPinSuccess
    ) {
      setResultState(false);
      handlePinSetSuccessAnalytics();
      refreshTiles();
      refreshSystemBanners();
    } else if (
      changeCardPinData &&
      (changeCardPinTypename ===
        ChangeCardPinResultTypenames.ChangeCardPinError ||
        changeCardPinTypename ===
          ChangeCardPinResultTypenames.ChangeCardPinIncorrectPinError ||
        changeCardPinTypename ===
          ChangeCardPinResultTypenames.ChangeCardPinMatchesOldError ||
        changeCardPinTypename ===
          ChangeCardPinResultTypenames.ChangeCardPinTooManyAttemptsError)
    ) {
      setResultState(false);

      if (pathName.includes(newPin)) {
        router.replace({
          pathname: `/myvaro/card/new-pin-error`,
          params: {
            title: changeCardPinData.title,
            message: changeCardPinData.message,
            resultType: changeCardPinData.__typename,
            cardId: cardId,
            cardType: cardType,
          },
        });
      } else if (pathName.includes(changePin)) {
        router.replace({
          pathname: `/myvaro/card/change-pin-error`,
          params: {
            title: changeCardPinData.title,
            message: changeCardPinData.message,
            cardId: cardId,
            cardType: cardType,
          },
        });
      } else {
        router.replace({
          pathname: `/myvaro/card/activate-card-error`,
          params: {
            title: changeCardPinData.title,
            message: changeCardPinData.message,
            cardType: cardType,
          },
        });
      }

      handlePinSetFailureAnalytics();
    } else {
      router.replace({
        pathname: `/myvaro/card/activate-card-error`,
        params: {
          cardType: cardType,
        },
      });
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText(pinTitle),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <BodyBaseText
        style={styles.text}
        numberOfLines={0}
        accessible
        testID={
          confirm
            ? 'card-pin-confirm-description'
            : 'card-pin-entry-description'
        }
      >
        {translatedText(description)}
      </BodyBaseText>

      <View style={styles.input}>
        {!confirm ? (
          <Pin
            pinLength={4}
            handleSubmit={() => {
              setConfirm(true);
              setDescription('confirmPinDescription');
            }}
            pin={pin}
            setPin={setPin}
            numPadSubmitButtonLabel={translatedText('next')}
            numPadSubmitButtonTestID="card-pin-next-button"
          />
        ) : (
          <Pin
            key={description}
            pinLength={4}
            handleSubmit={async () => {
              if (pin === confirmPin) {
                setHasError(false);
                const idempotencyId = getUuid();
                const input: ChangeCardPinInput = {
                  cardId: cardId,
                  oldPin: '',
                  newPin: confirmPin,
                };
                await cardPinChange(idempotencyId, input);
              } else {
                setHasError(true);
              }
            }}
            pin={confirmPin}
            setPin={setConfirmPin}
            numPadSubmitButtonLabel={translatedText('submit')}
            numPadSubmitButtonTestID="card-pin-submit-button"
            numPadSubmitButtonLoading={resultState}
            hasError={hasError}
            errorMessage={translatedText('pinMismatched')}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingBottom: theme.spacing.xs,
  },
  input: {
    flex: 1,
    marginHorizontal: theme.spacing.m,
  },
  text: {
    paddingLeft: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
});

export default PinSetScreen;
